import { Component, DestroyRef, inject, Input } from '@angular/core';
import { ProductCardComponent } from '../product-card/product-card.component';
import {
  BoelsBaseSite,
  BoelsCategoryHierarchy,
  CmsComponent,
  ProductSearchPage,
} from '@boels/models';

import {
  BehaviorSubject,
  combineLatest,
  filter,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import {
  CatalogsFacade,
  CultureFacade,
  GlobalProductSearchFacade,
  PrepareHtmlPipe,
  ProductSearchFacade,
  SiteContextFacade,
} from '@boels/shared-state';
import { SortingComponent } from '../product-sort/sorting.component';
import { TotalProductsComponent } from '../total-products/total-products.component';
import { BackToTopComponent } from '../back-to-top/back-to-top.component';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { SpinnerComponent } from '../spinner/spinner.component';
import {
  BreakpointService,
  DataDmAttributeDirective,
  EcommerceTrackingService,
  RoutingService,
} from '@boels/helpers';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { VerticalAlignerComponent } from '../vertical-aligner/vertical-aligner.component';

@Component({
  selector: 'boels-product-grid-component',
  templateUrl: './product-grid.component.html',
  styleUrls: ['./product-grid.component.scss'],
  imports: [
    AsyncPipe,
    ProductCardComponent,
    SortingComponent,
    TotalProductsComponent,
    BackToTopComponent,
    InfiniteScrollModule,
    PrepareHtmlPipe,
    SpinnerComponent,
    DataDmAttributeDirective,
    VerticalAlignerComponent,
    NgTemplateOutlet,
  ],
  providers: [GlobalProductSearchFacade, ProductSearchFacade],
})
export class ProductGridComponent {
  /** Injected services */
  #productSearchFacade = inject(ProductSearchFacade);
  #catalogFacade = inject(CatalogsFacade);
  #siteContextFacade = inject(SiteContextFacade);
  #cultureFacade = inject(CultureFacade);
  #globalProductSearchFacade = inject(GlobalProductSearchFacade);
  #ecommerceTrackingService = inject(EcommerceTrackingService);
  #routingService = inject(RoutingService);
  #destroyRef = inject(DestroyRef);
  #breakpointService = inject(BreakpointService);

  @Input()
  componentData: {
    data$: Observable<CmsComponent>;
  };

  private pagination$: BehaviorSubject<number | undefined> =
    new BehaviorSubject<number | undefined>(undefined);

  readonly $productSearchPage = toSignal(this.#productSearchPage());
  readonly $seo = toSignal(this.#seoData());
  readonly $productSearchPageLoading = toSignal(
    this.#productSearchPageLoading(),
  );

  private searchTerm: string | undefined = undefined;

  protected readonly isMobile = toSignal(this.#breakpointService.mdAndBelow$);

  onScroll(productSearchPage: ProductSearchPage): void {
    const currentPage = productSearchPage.pagination?.currentPage;
    const totalPages = productSearchPage.pagination?.totalPages;

    if (
      totalPages !== undefined &&
      currentPage !== undefined &&
      totalPages > currentPage
    ) {
      this.pagination$.next((currentPage ?? 0) + 1);
    }
  }

  showBackToTopButton(productSearchPage: ProductSearchPage): boolean {
    const totalPages = productSearchPage?.pagination?.totalPages;

    if (totalPages !== undefined) {
      const currentPage: number =
        productSearchPage?.pagination?.currentPage ?? 0;
      const isLastPage = currentPage >= totalPages - 1;
      return totalPages > 1 && isLastPage;
    }
    return false;
  }

  #productSearchPage(): Observable<ProductSearchPage | undefined> {
    return combineLatest([
      this.#routingService.activatedRouteSnapshot$.pipe(
        filter(Boolean),
        tap((activatedRoute) => {
          const currentPage = activatedRoute?.queryParams?.['currentPage'];
          this.pagination$.next(currentPage ?? 0);
        }),
      ),
      this.pagination$,
    ]).pipe(
      switchMap(([activatedRoute, currentPage]) => {
        if (
          this.#routingService
            .getRouteProperty('url')
            ?.some((urlSegment) => urlSegment.path === 'search')
        ) {
          return this.#globalProductSearchFacade
            .getGlobalProductsSearch(
              activatedRoute?.queryParams?.['query'],
              12,
              currentPage ?? 0,
              activatedRoute?.queryParams?.['sortCode'],
            )
            .pipe(
              tap((productSearchpage) => {
                if (this.searchTerm != productSearchpage?.freeTextSearch) {
                  this.#ecommerceTrackingService.setProductSearchEvent(
                    productSearchpage?.freeTextSearch || '',
                    productSearchpage?.pagination?.totalResults || 0,
                  );
                  this.searchTerm = productSearchpage?.freeTextSearch;
                }
              }),
            );
        } else if (activatedRoute?.params?.['categoryId']) {
          return this.#productSearchFacade.getProductsByCategoryId(
            activatedRoute?.params?.['categoryId'],
            currentPage ?? 0,
            activatedRoute?.queryParams?.['sortCode'],
            activatedRoute?.queryParams?.['query'],
          );
        }
        return of(undefined);
      }),
      takeUntilDestroyed(this.#destroyRef),
    );
  }

  #seoData(): Observable<BoelsCategoryHierarchy | undefined> {
    const routeParams = this.#routingService.getRouteProperty('params');
    return combineLatest([
      this.#siteContextFacade.getActiveBaseSiteEntity(),
      this.#cultureFacade.getActiveCulture(),
    ]).pipe(
      switchMap(([activeBaseSite, activeCulture]: [BoelsBaseSite, string]) =>
        this.#getCatalogData(
          activeBaseSite,
          activeCulture,
          routeParams?.['categoryId'],
        ),
      ),
    );
  }

  #productSearchPageLoading(): Observable<boolean> {
    return (
      this.#productSearchFacade.getProductSearchPageLoading() ||
      this.#globalProductSearchFacade.getGlobalProductSearchPageLoading()
    );
  }

  #getCatalogData(
    baseSite: BoelsBaseSite,
    culture: string,
    categoryId: string,
  ): Observable<BoelsCategoryHierarchy | undefined> {
    if (baseSite.uid) {
      return this.#catalogFacade.getCategoryForId(
        baseSite.uid,
        culture,
        categoryId,
      );
    }
    return of(undefined);
  }
}
